import { useCallback } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { useMultiDocumentUploaderContext } from '@/modules/documents/MultiDocumentUploader/context/multiDocumentUploader.context';
import { DocumentType } from '@/types/schema';

import { useDocumentUploadStepContext } from '../context/documentUploadStep.context';

/**
 * Persists the documents uploaded in the document-upload step (creating the
 * backend Document records with their chosen/inferred types). Used by the
 * step's Continue (footer), the background persister, and other exit actions.
 *
 * `persist()` saves only the files not already claimed this session, so it is
 * safe to call repeatedly — from concurrent callers or as more files finish
 * uploading — without duplicating Documents. It then waits for its own save AND
 * any concurrent save (e.g. the background persister's) to finish, returning
 * all created Document IDs when every pending save succeeded. Waiting on
 * concurrent saves matters because claims are synchronous: a caller can find
 * nothing left to claim yet another save is still in flight, and a caller that
 * navigates on success must not leave until that save lands.
 */
export function usePersistUploadedDocuments() {
  const {
    householdId,
    claimForPersist,
    releaseClaim,
    trackPersist,
    awaitInFlightPersists,
    recordPersistedDocumentIds,
    getPersistedDocumentIds,
  } = useDocumentUploadStepContext();
  const { uploadedFiles, uploadDocuments, setUploaderErrorMessage } =
    useMultiDocumentUploaderContext();
  const { reportError } = useReportError();
  const { showFeedback } = useFeedback();

  const persist = useCallback(async (): Promise<string[] | null> => {
    if (!householdId) return [];

    // Claim (synchronously) the uploaded files not yet saved this session, and
    // kick off their save. If none are new, another caller already claimed them
    // and we fall through to await that save below.
    const fileIds = claimForPersist(uploadedFiles.map((file) => file.fileId));
    if (fileIds.length > 0) {
      const run = (async (): Promise<boolean> => {
        setUploaderErrorMessage(undefined);
        try {
          const documentIds = await uploadDocuments({
            householdId,
            defaultDocumentType: DocumentType.Other,
            fileIds,
          });
          recordPersistedDocumentIds(documentIds);
          return true;
        } catch (error) {
          // Release the claim so the files can be retried.
          releaseClaim(fileIds);
          reportError('Error persisting onboarding documents', error as Error);
          setUploaderErrorMessage('Error saving documents. Please try again.');
          // The save can run in the background (after the user has advanced),
          // where the inline error isn't visible, so also surface a toast.
          showFeedback('Error saving documents. Please try again.');
          return false;
        }
      })();
      trackPersist(run);
    }

    const succeeded = await awaitInFlightPersists();
    return succeeded ? getPersistedDocumentIds() : null;
  }, [
    householdId,
    uploadedFiles,
    claimForPersist,
    releaseClaim,
    trackPersist,
    awaitInFlightPersists,
    recordPersistedDocumentIds,
    getPersistedDocumentIds,
    uploadDocuments,
    setUploaderErrorMessage,
    reportError,
    showFeedback,
  ]);

  return { persist };
}
